import { ReactNode } from "react";

export interface EntityGraphNode {
    key: string;
    label: string;
    typeId: number;
    typeLabel: string;
    typeOrder: number;
    leafChips: Array<string>;
}

export interface EntityGraphEdge {
    fromKey: string;
    toKey: string;
    kinds: Array<EntityGraphEdgeKind>;
    dashed: boolean;
}

export type EntityGraphDepth = 1 | 2 | "all";

// Node cards render their content in an HTML slot (SVG foreignObject), so a decoration can carry a real chip
// component; everything else stays data-only. The graph renders identically when no overlay is provided.
export interface EntityGraphNodeDecoration {
    fill?: string;
    stroke?: string;
    badgeText?: string;
    badgeTooltip?: string;
    chip?: ReactNode;
}

export interface EntityGraphOverlay {
    decorateNode?: (node: EntityGraphNode) => EntityGraphNodeDecoration | undefined;
    renderInspectorExtras?: (root: EntityGraphNode) => ReactNode;
}

export enum EntityGraphEdgeKind {
    UsesSystem = 1,
    DataProcessor = 2,
    Disclosure = 3,
    Source = 4,
    JointDataController = 5,
    GroupEntity = 6,
    Supplier = 7,
    SubProcessor = 8,
    AssociatedSystem = 9,
    UsesProcessingActivity = 10,
    LinkedLegalEntity = 11,
}

export enum EntityGraphEdgeProvenance {
    None = 0,
    Contextual = 1,
    Declared = 2,
}

export interface EntityGraphApiNode {
    type: number;
    id: string;
    name: string;
    linkChips: Array<string>;
}

export interface EntityGraphApiEdge {
    fromType: number;
    fromId: string;
    toType: number;
    toId: string;
    kind: EntityGraphEdgeKind;
    provenance: EntityGraphEdgeProvenance;
}

export interface EntityGraphApiRiskScore {
    type: number;
    id: string;
    riskLevel: number | null;
    overAppetiteCount: number;
    consequence: number | null;
    probability: number | null;
}

export interface EntityGraphApiRisk {
    riskAreaId: string;
    scale: number;
    appetite: number;
    riskLevels: Array<string>;
    riskLevelIndicators: Array<number>;
    consequenceLevels: Array<string>;
    probabilityLevels: Array<string>;
    scores: Array<EntityGraphApiRiskScore>;
}

export interface EntityGraphApiModel {
    rootType: number;
    rootId: string;
    nodes: Array<EntityGraphApiNode>;
    edges: Array<EntityGraphApiEdge>;
    risk: EntityGraphApiRisk | null;
}

export function entityGraphNodeKey(type: number, id: string) {
    return `${type}:${id}`;
}

export function parseEntityGraphNodeKey(key: string): { type: number; id: string } {
    const separatorIndex = key.indexOf(":");
    return { type: Number(key.slice(0, separatorIndex)), id: key.slice(separatorIndex + 1) };
}
